import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money, formatDateAT } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Download, Printer, TrendingUp, TrendingDown, Wallet, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { SkeletonKPICard, SkeletonBox } from "@/components/Skeleton";

export default function Reports() {
  const { t, lang } = useI18n();
  const [pnl, setPnl] = useState(null);
  const [dash, setDash] = useState(null);
  const [tax, setTax] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, d, x] = await Promise.all([
        api.get("/finance/pnl?months=12"),
        api.get("/finance/dashboard"),
        api.get("/finance/tax-summary"),
      ]);
      setPnl(p.data); setDash(d.data); setTax(x.data);
    })();
  }, []);

  const exportCsv = () => {
    if (!pnl?.months) return;
    const rows = [["month", "revenue", "expenses", "profit", "vat_collected"]];
    pnl.months.forEach((m) => rows.push([m.month, m.revenue, m.expenses, m.profit, m.vat_collected]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pnl-12m-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 space-y-8 max-w-[1400px] mx-auto print-full">
      <PageHeader
        title={t("reports")}
        description={`${lang === "tr" ? "Finansal analiz — 12 aylık P&L, KDV özeti, kategori dağılımı" : "Finanzanalyse — 12M G&V, MwSt., Kategorien"} · ${formatDateAT(new Date().toISOString())}`}
      >
        <Button variant="outline" size="sm" onClick={() => window.print()} className="h-10 rounded-lg no-print">
          <Printer className="w-4 h-4 mr-2" /> {lang === "tr" ? "Yazdır" : "Drucken"}
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} className="h-10 rounded-lg no-print" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" /> CSV
        </Button>
      </PageHeader>

      {/* Summary */}
      {pnl ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <SummaryStat label={`${t("revenue")} · 12M`} value={money(pnl.total_revenue)} icon={TrendingUp} tone="primary" />
          <SummaryStat label={`${t("expenses")} · 12M`} value={money(pnl.total_expenses)} icon={TrendingDown} tone="destructive" />
          <SummaryStat label={t("profitLoss")} value={money(pnl.total_profit)} icon={Wallet} tone="emerald" accent />
        </div>
      ) : <div className="grid grid-cols-3 gap-3">{[0,1,2].map((i) => <SkeletonKPICard key={i} />)}</div>}

      {/* P&L 12M */}
      <Card className="border rounded-xl shadow-elev-1">
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base font-display font-semibold tracking-tight">{t("profitLoss")}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">12 {lang === "tr" ? "ay" : "Monate"}</p>
          </div>
          <Badge variant="secondary" className="font-mono text-[10px] rounded-md">12M</Badge>
        </CardHeader>
        <CardContent className="h-80 pt-2">
          {pnl?.months ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pnl.months} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v}`} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => money(v)} cursor={{ fill: "hsl(var(--muted) / 0.3)" }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                <Bar dataKey="revenue" name={t("revenue")} fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={28} />
                <Bar dataKey="expenses" name={t("expenses")} fill="hsl(0 84% 60%)" radius={[6, 6, 0, 0]} maxBarSize={28} />
                <Bar dataKey="profit" name={lang === "tr" ? "Kâr" : "Gewinn"} fill="hsl(158 64% 42%)" radius={[6, 6, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : <SkeletonBox className="h-full w-full" />}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Expense breakdown pie */}
        <Card className="border rounded-xl shadow-elev-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display font-semibold tracking-tight">{t("expenseBreakdown")}</CardTitle>
            <p className="text-xs text-muted-foreground">{t("thisMonth")}</p>
          </CardHeader>
          <CardContent className="pt-0">
            {dash?.expense_breakdown?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dash.expense_breakdown}
                        dataKey="amount"
                        nameKey={(d) => d.name?.[lang] || d.name?.tr || "—"}
                        innerRadius={54}
                        outerRadius={90}
                        paddingAngle={2}
                        stroke="hsl(var(--background))"
                        strokeWidth={2}
                      >
                        {dash.expense_breakdown.map((c, i) => (
                          <Cell key={i} fill={c.color || "#94A3B8"} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {dash.expense_breakdown.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: c.color }} />
                        <span className="truncate">{c.name?.[lang] || c.name?.tr}</span>
                      </div>
                      <span className="font-mono font-semibold shrink-0 ml-2">{money(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground py-16 text-center">{t("empty")}</p>}
          </CardContent>
        </Card>

        {/* Tax summary */}
        <Card className="border rounded-xl shadow-elev-1 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl pointer-events-none" />
          <CardHeader className="pb-3 relative">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base font-display font-semibold tracking-tight">{t("quarterlyVat")}</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">{tax?.period || "…"}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 relative">
            {tax ? (
              <>
                <Row label={t("revenue")} value={money(tax.revenue_gross)} />
                <Row label={t("outputVat")} value={money(tax.output_vat)} />
                <Row label={t("inputVat")} value={money(tax.input_vat)} />
                <div className="border-t pt-3 mt-2">
                  <Row label={t("netVat")} value={money(tax.net_vat_payable)} accent />
                </div>
              </>
            ) : <SkeletonBox className="h-24 w-full" />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, icon: Icon, tone, accent }) {
  const toneClass = {
    primary: "bg-primary/10 text-primary",
    destructive: "bg-destructive/10 text-destructive",
    emerald: "bg-emerald-500/10 text-emerald-600",
  }[tone] || "bg-muted";
  return (
    <div className="border rounded-xl p-5 bg-background shadow-elev-1 card-hover animate-fade-rise">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</span>
        {Icon && <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneClass}`}><Icon className="w-4 h-4" /></div>}
      </div>
      <div className={`font-mono text-2xl md:text-[26px] font-semibold tracking-[-0.02em] ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between font-mono text-sm items-baseline">
      <span className={accent ? "font-semibold text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className={`font-semibold ${accent ? "text-primary text-lg" : ""}`}>{value}</span>
    </div>
  );
}
